import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, RefreshCw, DollarSign, Building2, Phone, Mail, Globe, Clock, Landmark, Send, AlertTriangle, Truck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

export function SettingsTab({ getAuthHeaders, dashFeatures, setDashFeatures }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bankRunEnabled, setBankRunEnabled] = useState(true);
  const [bankRunInfo, setBankRunInfo] = useState({});
  const [bankRunLoading, setBankRunLoading] = useState(false);
  const [repriceDialog, setRepriceDialog] = useState({ open: false, loading: false, preview: null, confirming: false, result: null });

  const openRepriceDialog = async () => {
    setRepriceDialog({ open: true, loading: true, preview: null, confirming: false, result: null });
    try {
      const res = await axios.post(`${API}/api/admin/bookings/reprice-return-and-notify?dry_run=true`, {}, getAuthHeaders());
      setRepriceDialog(s => ({ ...s, loading: false, preview: res.data }));
    } catch (e) {
      toast.error("Failed to load reprice preview");
      setRepriceDialog({ open: false, loading: false, preview: null, confirming: false, result: null });
    }
  };

  const confirmReprice = async () => {
    setRepriceDialog(s => ({ ...s, confirming: true }));
    try {
      const res = await axios.post(`${API}/api/admin/bookings/reprice-return-and-notify?dry_run=false`, {}, getAuthHeaders());
      const sent = res.data.notifications_sent ?? 0;
      toast.success(`Repriced ${res.data.changed_count} bookings, sent ${sent} WhatsApp notifications`);
      setRepriceDialog(s => ({ ...s, confirming: false, result: res.data }));
    } catch (e) {
      toast.error("Reprice failed — check backend logs");
      setRepriceDialog(s => ({ ...s, confirming: false }));
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchBankRunSettings();
  }, []);

  const fetchBankRunSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/bank-run/settings`, getAuthHeaders());
      setBankRunEnabled(!!res.data.enabled);
      setBankRunInfo(res.data);
    } catch {/* ignore */}
  };

  const toggleBankRun = async (newVal) => {
    setBankRunLoading(true);
    const prev = bankRunEnabled;
    setBankRunEnabled(newVal);
    try {
      await axios.post(`${API}/admin/bank-run/toggle`, { enabled: newVal }, getAuthHeaders());
      toast.success(newVal ? "Bank Run service turned ON" : "Bank Run service turned OFF");
      await fetchBankRunSettings();
    } catch (e) {
      setBankRunEnabled(prev);
      toast.error("Failed to toggle Bank Run");
    } finally {
      setBankRunLoading(false);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/settings`, getAuthHeaders());
      setSettings(res.data);
    } catch (e) {
      toast.error("Failed to load settings");
    }
    setLoading(false);
  };

  const savePricing = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { pricing: settings.pricing }, getAuthHeaders());
      toast.success("Pricing updated!");
    } catch (e) {
      toast.error("Failed to save pricing");
    }
    setSaving(false);
  };

  const saveBusinessInfo = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { business_info: settings.business_info }, getAuthHeaders());
      toast.success("Business info updated!");
    } catch (e) {
      toast.error("Failed to save business info");
    }
    setSaving(false);
  };

  const saveBookingRules = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { booking_rules: settings.booking_rules }, getAuthHeaders());
      toast.success("Booking rules updated!");
    } catch (e) {
      toast.error("Failed to save booking rules");
    }
    setSaving(false);
  };

  const updatePricing = (zone, field, value) => {
    setSettings(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [zone]: {
          ...prev.pricing[zone],
          [field]: parseFloat(value) || 0
        }
      }
    }));
  };

  const updateBusinessInfo = (field, value) => {
    setSettings(prev => ({
      ...prev,
      business_info: {
        ...prev.business_info,
        [field]: value
      }
    }));
  };

  if (loading || !settings) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="w-8 h-8 mx-auto animate-spin text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Loading settings...</p>
      </div>
    );
  }

  const p = settings.pricing || {};
  const b = settings.business_info || {};
  const br = settings.booking_rules || { min_booking_hours: 48 };

  return (
    <div className="space-y-6" data-testid="settings-tab">
      {/* Bank Run On/Off */}
      <Card className="border-0 shadow-lg rounded-2xl" data-testid="bank-run-toggle-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Landmark className="w-5 h-5 text-blue-600" />
            Bank Run Service
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
            <div className="min-w-0">
              <p className="font-semibold flex items-center gap-2">
                {bankRunEnabled ? "🟢 Service is LIVE" : "⏸ Service is PAUSED"}
              </p>
              {bankRunInfo.auto_paused_for && (
                <p className="text-xs text-orange-700 mt-1">
                  🤖 Auto-paused for federal holiday: <b>{bankRunInfo.today_holiday || bankRunInfo.auto_paused_for}</b>. Will resume automatically tomorrow.
                </p>
              )}
              {bankRunInfo.paused_by_admin && (
                <p className="text-xs text-red-700 mt-1">
                  🛑 Paused by admin — toggle ON to resume.
                </p>
              )}
              {bankRunInfo.next_holiday && bankRunEnabled && (
                <p className="text-xs text-yellow-800 mt-1">
                  ⚠️ Upcoming: Bank Run will auto-pause on <b>{bankRunInfo.next_holiday.date}</b> for {bankRunInfo.next_holiday.name}.
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Bank Runs run <b>Wed &amp; Thu, 8 AM – 12 PM CT</b> · $60 flat (1-4 pax) · paid in cash to driver.
                Auto-pauses on US federal banking holidays. Toggle off to remove from the website immediately.
              </p>
            </div>
            <Switch
              checked={bankRunEnabled}
              disabled={bankRunLoading}
              onCheckedChange={toggleBankRun}
              data-testid="bank-run-toggle-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* One-shot: Reprice All Return Trips + Notify */}
      <Card className="border-0 shadow-lg rounded-2xl border-l-4 border-l-indigo-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Send className="w-5 h-5 text-indigo-600" />
            Reprice Return Trips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700 mb-3">
            Re-runs the latest pricing rules against every active future return trip (Madison $65, ORD/MDW/MKE $200 solo → $85 shared, $5 per extra suitcase, first free) and sends each affected passenger a WhatsApp confirmation with their updated price.
          </p>
          <Button
            onClick={openRepriceDialog}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
            data-testid="reprice-return-trips-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Preview &amp; Send Updates
          </Button>
          <Button
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/api/admin/return-trips/adjust-pickup-and-notify?dry_run=true`, {}, getAuthHeaders());
                const changes = res.data.pickup_changes || 0;
                const notify = res.data.notifications_would_send || 0;
                if (!changes) { toast.info("No pickup times need adjustment."); return; }
                if (!confirm(`Adjust ${changes} return pickup times & send ${notify} WhatsApp confirmations to paid passengers?`)) return;
                const done = await axios.post(`${API}/api/admin/return-trips/adjust-pickup-and-notify?dry_run=false`, {}, getAuthHeaders());
                toast.success(`Updated ${done.data.pickup_changes}, sent ${done.data.notifications_sent} WhatsApp`);
              } catch (e) { toast.error("Adjust failed"); }
            }}
            className="ml-2 bg-purple-600 hover:bg-purple-700 text-white rounded-full"
            data-testid="adjust-pickup-btn"
          >
            <Clock className="w-4 h-4 mr-2" />
            Auto-Adjust Pickup Times
          </Button>
        </CardContent>
      </Card>

      {/* Reprice Dialog */}
      <Dialog open={repriceDialog.open} onOpenChange={(o) => !o && setRepriceDialog({ open: false, loading: false, preview: null, confirming: false, result: null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reprice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-indigo-600" />
              Reprice Return Trips
            </DialogTitle>
            <DialogDescription>
              Review affected passengers before sending WhatsApp notifications.
            </DialogDescription>
          </DialogHeader>

          {repriceDialog.loading && (
            <div className="py-8 text-center text-sm text-gray-600" data-testid="reprice-loading">
              <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
              Analyzing return bookings…
            </div>
          )}

          {repriceDialog.preview && !repriceDialog.result && (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div>Total future return bookings scanned: <b>{repriceDialog.preview.total_return_bookings_scanned}</b></div>
                <div>Bookings with price change: <b data-testid="reprice-changed-count">{repriceDialog.preview.changed_count}</b></div>
                <div>WhatsApp notifications to send: <b>{repriceDialog.preview.notifications_would_send}</b></div>
              </div>
              {repriceDialog.preview.changed_count === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
                  Every return booking is already at the correct price. Nothing to update.
                </div>
              ) : (
                <div className="border rounded-lg divide-y" data-testid="reprice-affected-list">
                  {repriceDialog.preview.affected.map((row) => (
                    <div key={row.id} className="p-3 text-sm flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900">{row.full_name} <span className="text-xs text-gray-500 ml-1">{row.confirmation}</span></div>
                        <div className="text-xs text-gray-600">{row.travel_date} · {row.travel_time} → {row.dropoff}</div>
                        <div className="text-xs">
                          <span className="line-through text-gray-500">${row.old_total.toFixed(0)}</span>{" → "}
                          <span className="font-bold text-emerald-700">${row.new_total.toFixed(0)}</span>
                          <span className={`ml-2 ${row.delta > 0 ? "text-red-600" : "text-emerald-700"}`}>
                            ({row.delta > 0 ? "+" : ""}${row.delta.toFixed(0)})
                          </span>
                          {row.is_shared && <span className="ml-2 text-emerald-700 font-bold">SHARED</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {repriceDialog.preview.changed_count > 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    Clicking <b>Confirm &amp; Send</b> will write the new prices to the DB and fire {repriceDialog.preview.notifications_would_send} WhatsApp messages via Twilio (SMS/email fallback if WhatsApp is over sandbox limit). This can&apos;t be undone.
                  </div>
                </div>
              )}
            </div>
          )}

          {repriceDialog.result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm" data-testid="reprice-result">
              <div className="font-bold text-emerald-800 mb-2">✅ Done</div>
              <div>Bookings repriced: <b>{repriceDialog.result.changed_count}</b></div>
              <div>WhatsApp notifications sent: <b>{repriceDialog.result.notifications_sent}</b></div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRepriceDialog({ open: false, loading: false, preview: null, confirming: false, result: null })} data-testid="reprice-close-btn">
              Close
            </Button>
            {repriceDialog.preview && !repriceDialog.result && repriceDialog.preview.changed_count > 0 && (
              <Button
                onClick={confirmReprice}
                disabled={repriceDialog.confirming}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="reprice-confirm-btn"
              >
                {repriceDialog.confirming ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Confirm &amp; Send {repriceDialog.preview.notifications_would_send} Messages</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing Settings */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <DollarSign className="w-5 h-5 text-green-600" />
              Pricing Configuration
            </CardTitle>
            <Button onClick={savePricing} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="save-pricing-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Pricing"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Arrival Pricing */}
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-3">Arrival Pricing (Airport → Wisconsin)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { zone: "ohare", label: "O'Hare" },
                { zone: "midway", label: "Midway" },
                { zone: "milwaukee", label: "Milwaukee" },
                { zone: "madison", label: "Madison" }
              ].map(({ zone, label }) => (
                <div key={zone} className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <p className="font-semibold text-sm">{label}</p>
                  <div>
                    <Label className="text-xs text-muted-foreground">Shared ($/person)</Label>
                    <Input
                      type="number" step="0.01"
                      value={p[zone]?.shared || 0}
                      onChange={(e) => updatePricing(zone, "shared", e.target.value)}
                      className="h-9" data-testid={`pricing-${zone}-shared`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">No Deposit ($/person)</Label>
                    <Input
                      type="number" step="0.01"
                      value={p[zone]?.no_deposit || 0}
                      onChange={(e) => updatePricing(zone, "no_deposit", e.target.value)}
                      className="h-9" data-testid={`pricing-${zone}-no-deposit`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Private (flat rate)</Label>
                    <Input
                      type="number" step="0.01"
                      value={p[zone]?.private || 0}
                      onChange={(e) => updatePricing(zone, "private", e.target.value)}
                      className="h-9" data-testid={`pricing-${zone}-private`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Return Pricing */}
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-3">Return Pricing (Wisconsin → Airport)</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-xl p-4">
                <Label className="text-xs text-muted-foreground">Solo / Private ($)</Label>
                <Input
                  type="number" step="0.01"
                  value={p.return?.solo || p.return?.private || 200}
                  onChange={(e) => updatePricing("return", "solo", e.target.value)}
                  className="h-9 mt-1" data-testid="pricing-return-solo"
                />
                <span className="text-[10px] text-muted-foreground">1 passenger only</span>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <Label className="text-xs text-muted-foreground">Shared Per Person ($)</Label>
                <Input
                  type="number" step="0.01"
                  value={p.return?.shared || p.return?.per_person || 85}
                  onChange={(e) => updatePricing("return", "shared", e.target.value)}
                  className="h-9 mt-1" data-testid="pricing-return-shared"
                />
                <span className="text-[10px] text-muted-foreground">2+ pax same date/airport</span>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <Label className="text-xs text-muted-foreground">Madison Solo ($)</Label>
                <Input
                  type="number" step="0.01"
                  value={p.return?.madison_solo || 85}
                  onChange={(e) => updatePricing("return", "madison_solo", e.target.value)}
                  className="h-9 mt-1" data-testid="pricing-return-madison-solo"
                />
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <Label className="text-xs text-muted-foreground">Madison Shared ($)</Label>
                <Input
                  type="number" step="0.01"
                  value={p.return?.madison_shared || 65}
                  onChange={(e) => updatePricing("return", "madison_shared", e.target.value)}
                  className="h-9 mt-1" data-testid="pricing-return-madison-shared"
                />
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <Label className="text-xs text-muted-foreground">Per Suitcase ($)</Label>
                <Input
                  type="number" step="0.01"
                  value={p.return?.per_suitcase || 0}
                  onChange={(e) => updatePricing("return", "per_suitcase", e.target.value)}
                  className="h-9 mt-1" data-testid="pricing-return-per-suitcase"
                />
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <Label className="text-xs text-muted-foreground">Per Barrel ($)</Label>
                <Input
                  type="number" step="0.01"
                  value={p.return?.per_barrel || 0}
                  onChange={(e) => updatePricing("return", "per_barrel", e.target.value)}
                  className="h-9 mt-1" data-testid="pricing-return-per-barrel"
                />
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <Label className="text-xs text-muted-foreground">With Shipping Co ($)</Label>
                <Input
                  type="number" step="0.01"
                  value={p.return?.shipping_company || 0}
                  onChange={(e) => updatePricing("return", "shipping_company", e.target.value)}
                  className="h-9 mt-1" data-testid="pricing-return-shipping"
                />
              </div>
            </div>
          </div>

          {/* Deposit */}
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-3">Deposit</h4>
            <div className="bg-gray-50 rounded-xl p-4 max-w-xs">
              <Label className="text-xs text-muted-foreground">Deposit Per Person ($)</Label>
              <Input
                type="number" step="0.01"
                value={p.deposit_per_person || 0}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  pricing: { ...prev.pricing, deposit_per_person: parseFloat(e.target.value) || 0 }
                }))}
                className="h-9 mt-1" data-testid="pricing-deposit-per-person"
              />
            </div>
          </div>

          {/* T&M Courier Shipping Prices */}
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4 text-orange-600" /> T&M Courier — Shipping Prices
            </h4>
            <p className="text-xs text-muted-foreground mb-3">Per-item rates charged by T&M Courier for luggage/cargo shipping</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: "ship_suitcase", label: "Per Suitcase ($)", icon: "🧳" },
                { key: "ship_barrel", label: "Per Barrel ($)", icon: "🛢️" },
                { key: "ship_box", label: "Per Box ($)", icon: "📦" },
                { key: "ship_bicycle", label: "Per Bicycle ($)", icon: "🚲" },
                { key: "ship_scooter", label: "Per Scooter ($)", icon: "🛴" },
                { key: "ship_tv", label: "Per TV ($)", icon: "📺" },
                { key: "ship_misc", label: "Misc Item ($)", icon: "📋" },
              ].map(({ key, label, icon }) => (
                <div key={key} className="bg-orange-50 rounded-xl p-3 border border-orange-100">
                  <Label className="text-xs text-muted-foreground">{icon} {label}</Label>
                  <Input
                    type="number" step="0.01"
                    value={p.shipping?.[key] || 0}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      pricing: {
                        ...prev.pricing,
                        shipping: {
                          ...prev.pricing.shipping,
                          [key]: parseFloat(e.target.value) || 0
                        }
                      }
                    }))}
                    className="h-9 mt-1" data-testid={`pricing-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Booking Rules */}
      <Card className="border-0 shadow-lg rounded-2xl" data-testid="booking-rules-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Clock className="w-5 h-5 text-amber-600" />
              Booking Rules
            </CardTitle>
            <Button onClick={saveBookingRules} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-booking-rules-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Rules"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-amber-900">
              Customers attempting to book within this window are <span className="font-bold">blocked from completing a booking online</span> and shown a banner directing them to WhatsApp/phone for last-minute requests.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" /> Minimum Advance Booking (hours)
              </Label>
              <Input
                type="number"
                min="0"
                max="240"
                value={br.min_booking_hours ?? 48}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  booking_rules: { ...prev.booking_rules, min_booking_hours: parseInt(e.target.value) || 0 }
                }))}
                className="h-10 mt-1"
                data-testid="min-booking-hours-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Default: 48. Set to 0 to disable.</p>
            </div>
            <div className="text-sm text-muted-foreground">
              <p><span className="font-semibold">Tip:</span> Drop to <span className="font-semibold text-amber-700">24h</span> in slow seasons to capture last-minute bookings, or raise to <span className="font-semibold text-amber-700">72h</span> before holidays to ensure driver availability.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Info */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Building2 className="w-5 h-5 text-blue-600" />
              Business Information
            </CardTitle>
            <Button onClick={saveBusinessInfo} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-business-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Info"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Building2 className="w-3 h-3" /> Company Name
              </Label>
              <Input
                value={b.company_name || ""}
                onChange={(e) => updateBusinessInfo("company_name", e.target.value)}
                className="h-9 mt-1" data-testid="business-company-name"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Phone className="w-3 h-3" /> Phone
              </Label>
              <Input
                value={b.phone || ""}
                onChange={(e) => updateBusinessInfo("phone", e.target.value)}
                className="h-9 mt-1" data-testid="business-phone"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Mail className="w-3 h-3" /> Email
              </Label>
              <Input
                value={b.email || ""}
                onChange={(e) => updateBusinessInfo("email", e.target.value)}
                className="h-9 mt-1" data-testid="business-email"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Globe className="w-3 h-3" /> Website
              </Label>
              <Input
                value={b.website || ""}
                onChange={(e) => updateBusinessInfo("website", e.target.value)}
                className="h-9 mt-1" data-testid="business-website"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-muted-foreground">Tagline</Label>
              <Input
                value={b.tagline || ""}
                onChange={(e) => updateBusinessInfo("tagline", e.target.value)}
                className="h-9 mt-1" data-testid="business-tagline"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dashboard Feature Toggles */}
      <Card className="border-0 shadow-lg rounded-2xl" data-testid="dashboard-features-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Globe className="w-5 h-5 text-violet-600" />
            Dashboard Features
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
            <div>
              <Label className="font-semibold text-sm">Passengers by Flight Chart</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Show flight grouping chart on the Overview tab</p>
            </div>
            <Switch
              checked={dashFeatures?.show_flight_chart || false}
              onCheckedChange={async (val) => {
                const newFeatures = { ...dashFeatures, show_flight_chart: val };
                setDashFeatures(newFeatures);
                try {
                  await axios.put(`${API}/admin/settings`, { dashboard_features: newFeatures }, getAuthHeaders());
                  toast.success(val ? "Flight chart enabled" : "Flight chart hidden");
                } catch (e) {
                  toast.error("Failed to save");
                }
              }}
              data-testid="toggle-flight-chart"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
